import { useCallback, useMemo, useRef, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { Alert, Badge, Button, Group, Loader, Pagination, ScrollArea, Stack, Table, Text } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconAlertCircle, IconFileSpreadsheet, IconUpload } from '@tabler/icons-react';
import { useTheme } from '@contexts/ThemeContext';
import { useLanguage } from '@contexts/LanguageContext';
import { importErrorMessage, useApplyConsigneeImport, usePreviewConsigneeImport } from './api';
import { usePpjkEvent } from '@/features/realtime/ppjkEvents';
import { createOwnWriteGuard } from './ownWriteGuard';
import {
  jenisApiLabel,
  statusLabel,
  type ImportAction,
  type ImportPreview,
  type ImportResult,
  type ImportRow,
} from './types';

export interface ConsigneeImportPanelProps {
  /** `modal` sits inside the Master page dialog; `wizard` inside the onboarding step. Same flow, tighter chrome. */
  mode: 'modal' | 'wizard';
  /** Fires after a successful apply, with the server's counts. */
  onImported?: (result: ImportResult) => void;
  /** Apply lifecycle, for a host that must tell its own import from a colleague's. */
  onApplyStateChange?: (state: 'start' | 'success' | 'error') => void;
}

const ROWS_PER_PAGE = 50;

const XLSX_ACCEPT = {
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
};

const fill = (hex: string) => `${hex}22`;

const fieldLabelKey: Record<string, string> = {
  nama: 'consignee.colNama',
  alamat: 'consignee.colAlamat',
  nib: 'consignee.colNib',
  kodeJenisApi: 'consignee.colJenisApi',
  kodeStatus: 'consignee.colStatus',
};

const displayValue = (field: string, value: string): string => {
  if (field === 'kodeJenisApi') return jenisApiLabel(value) || value;
  if (field === 'kodeStatus') return statusLabel(value) || value;
  return value;
};

/**
 * Download template, drop the filled sheet, review what will happen row by row,
 * then import. Nothing is written until the user presses the import button, and
 * an import never deletes: rows missing from the sheet are left alone.
 */
export default function ConsigneeImportPanel({ mode, onImported, onApplyStateChange }: ConsigneeImportPanelProps) {
  const { colors: themeColors } = useTheme();
  const { t } = useLanguage();

  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<ImportPreview | null>(null);
  const [result, setResult] = useState<ImportResult | null>(null);
  const [page, setPage] = useState(1);
  const [dropError, setDropError] = useState<string | null>(null);

  const previewMutation = usePreviewConsigneeImport();
  const applyMutation = useApplyConsigneeImport();

  const actionColor: Record<ImportAction, string> = {
    new: themeColors.success,
    updated: themeColors.warning,
    unchanged: themeColors.textSecondary,
    rejected: themeColors.error,
  };

  const actionLabel: Record<ImportAction, string> = {
    new: t('consigneeImport.actionNew'),
    updated: t('consigneeImport.actionUpdated'),
    unchanged: t('consigneeImport.actionUnchanged'),
    rejected: t('consigneeImport.actionRejected'),
  };

  // Someone in the PPJK changed the master after this preview was computed.
  const [previewStale, setPreviewStale] = useState(false);

  // This panel's own applies, so their broadcast is not mistaken for a colleague's.
  const ownApply = useRef(createOwnWriteGuard()).current;

  usePpjkEvent('consignee_master_changed', () => {
    // Our own import also produces this event, while applying or shortly after.
    if (ownApply.isSuppressed()) return;
    if (preview && !result && !applyMutation.isPending) setPreviewStale(true);
  });

  const reset = useCallback(() => {
    setFile(null);
    setPreview(null);
    setResult(null);
    setPage(1);
    setDropError(null);
    setPreviewStale(false);
    previewMutation.reset();
    applyMutation.reset();
  }, [previewMutation, applyMutation]);

  const onDrop = useCallback(
    (accepted: File[]) => {
      const next = accepted[0];
      if (!next) return;
      setDropError(null);
      setResult(null);
      setPage(1);
      setFile(next);
      setPreviewStale(false);
      previewMutation.mutate(next, {
        onSuccess: (data) => setPreview(data),
        onError: () => setPreview(null),
      });
    },
    [previewMutation]
  );

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    accept: XLSX_ACCEPT,
    maxFiles: 1,
    multiple: false,
    noClick: true,
    disabled: previewMutation.isPending || applyMutation.isPending,
    onDrop,
    onDropRejected: () => setDropError(t('consigneeImport.onlyXlsx')),
  });

  const importable = preview ? preview.summary.new + preview.summary.updated : 0;

  const pagedRows = useMemo(() => {
    if (!preview) return [];
    const start = (page - 1) * ROWS_PER_PAGE;
    return preview.rows.slice(start, start + ROWS_PER_PAGE);
  }, [preview, page]);

  const totalPages = preview ? Math.max(1, Math.ceil(preview.rows.length / ROWS_PER_PAGE)) : 1;

  const handleApply = () => {
    if (!file) return;
    ownApply.begin();
    onApplyStateChange?.('start');
    applyMutation.mutate(file, {
      onSuccess: (data) => {
        ownApply.succeed();
        onApplyStateChange?.('success');
        setResult(data);
        notifications.show({
          title: t('consigneeImport.importedTitle'),
          message: t('consigneeImport.importedMsg')
            .replace('{new}', String(data.imported.new))
            .replace('{updated}', String(data.imported.updated)),
          color: 'green',
        });
        onImported?.(data);
      },
      onError: (error) => {
        ownApply.fail();
        onApplyStateChange?.('error');
        notifications.show({
          title: t('consigneeImport.failedTitle'),
          message: importErrorMessage(error, t('consigneeImport.failedMsg')),
          color: 'red',
        });
      },
    });
  };

  const pill = (action: ImportAction, count?: number) => (
    <Badge
      variant="light"
      radius="xl"
      styles={{
        root: {
          backgroundColor: fill(actionColor[action]),
          color: actionColor[action],
          textTransform: 'none',
          fontWeight: 600,
          // Inside a nowrap table cell the badge is a flex child; without this it
          // shrinks to its first letter instead of keeping the whole word.
          flexShrink: 0,
          maxWidth: 'none',
          overflow: 'visible',
        },
      }}
    >
      {typeof count === 'number' ? `${count.toLocaleString()} ${actionLabel[action]}` : actionLabel[action]}
    </Badge>
  );

  const cellText = { color: themeColors.text, fontSize: 13 } as const;
  // globals.css forces Inter everywhere (!important); tabular numerals keep digit columns aligned.
  const monoStyle = { ...cellText, fontVariantNumeric: 'tabular-nums', letterSpacing: '0.02em' } as const;

  const renderChanged = (row: ImportRow, field: string, current: string) => {
    const change = row.changes?.find((c) => c.field === field);
    if (!change) return <span style={cellText}>{displayValue(field, current)}</span>;
    return (
      <span>
        <span style={{ color: themeColors.textSecondary, textDecoration: 'line-through', marginRight: 6 }}>
          {displayValue(field, change.old) || '-'}
        </span>
        <span style={{ color: themeColors.warning, fontWeight: 600 }}>{displayValue(field, change.new)}</span>
      </span>
    );
  };

  const dropzone = (
    <div
      {...getRootProps()}
      style={{
        border: `1.5px dashed ${isDragActive ? themeColors.accent : themeColors.border}`,
        backgroundColor: isDragActive ? fill(themeColors.accent) : themeColors.formFields,
        borderRadius: 10,
        padding: mode === 'wizard' ? 28 : 22,
        textAlign: 'center',
        transition: 'border-color 150ms, background-color 150ms',
      }}
    >
      <input {...getInputProps()} />
      <Stack gap={6} align="center">
        <IconFileSpreadsheet size={28} stroke={1.5} style={{ color: themeColors.accent }} />
        <Text fw={600} style={{ color: themeColors.text }}>
          {t('consigneeImport.dropTitle')}
        </Text>
        <Text size="sm" style={{ color: themeColors.textSecondary }}>
          {t('consigneeImport.dropHint')}
        </Text>
        <Button
          size="xs"
          variant="light"
          leftSection={<IconUpload size={14} />}
          onClick={open}
          disabled={previewMutation.isPending || applyMutation.isPending}
        >
          {t('consigneeImport.chooseFile')}
        </Button>
      </Stack>
    </div>
  );

  if (result) {
    return (
      <Stack gap="md">
        <Alert color="green" icon={<IconFileSpreadsheet size={16} />} title={t('consigneeImport.importedTitle')}>
          {t('consigneeImport.resultLine')
            .replace('{new}', String(result.imported.new))
            .replace('{updated}', String(result.imported.updated))
            .replace('{unchanged}', String(result.imported.unchanged))}
        </Alert>
        {result.rejected.length > 0 && (
          <Alert color="red" icon={<IconAlertCircle size={16} />} title={t('consigneeImport.rejectedTitle')}>
            <Stack gap={4}>
              {result.rejected.map((row) => (
                <Text key={row.rowNumber} size="sm">
                  {t('consigneeImport.rowLabel').replace('{n}', String(row.rowNumber))}: {row.nama || row.npwp}{' '}
                  {row.reason ? `(${row.reason})` : ''}
                </Text>
              ))}
            </Stack>
          </Alert>
        )}
        <Group justify="flex-end">
          <Button
            variant="outline"
            onClick={reset}
            styles={{ root: { borderColor: themeColors.border, color: themeColors.text } }}
          >
            {t('consigneeImport.uploadAnother')}
          </Button>
        </Group>
      </Stack>
    );
  }

  return (
    <Stack gap="md">
      {!preview && dropzone}

      {dropError && (
        <Alert color="red" icon={<IconAlertCircle size={16} />}>
          {dropError}
        </Alert>
      )}

      {previewMutation.isPending && (
        <Group gap="xs">
          <Loader size="xs" />
          <Text size="sm" style={{ color: themeColors.textSecondary }}>
            {t('consigneeImport.reading').replace('{file}', file?.name ?? '')}
          </Text>
        </Group>
      )}

      {previewMutation.isError && (
        <Alert color="red" icon={<IconAlertCircle size={16} />} title={t('consigneeImport.previewFailed')}>
          {importErrorMessage(previewMutation.error, t('consigneeImport.failedMsg'))}
          <Group mt="sm">
            <Button size="xs" variant="outline" onClick={reset}>
              {t('consigneeImport.chooseOther')}
            </Button>
          </Group>
        </Alert>
      )}

      {preview && previewStale && (
        <Alert color="yellow" variant="light" icon={<IconAlertCircle size={16} />} data-preview-stale>
          <Group justify="space-between" gap="sm" wrap="wrap">
            <Text size="sm">{t('consigneeImport.previewStale')}</Text>
            <Button
              size="xs"
              variant="light"
              color="yellow"
              loading={previewMutation.isPending}
              onClick={() => {
                if (!file) return;
                previewMutation.mutate(file, {
                  onSuccess: (data) => {
                    setPreview(data);
                    setPage(1);
                    setPreviewStale(false);
                  },
                });
              }}
            >
              {t('consigneeImport.reloadPreview')}
            </Button>
          </Group>
        </Alert>
      )}

      {preview && (
        <div style={{ border: `1px solid ${themeColors.border}`, borderRadius: 10, overflow: 'hidden' }}>
          <Group
            gap="sm"
            wrap="wrap"
            px="md"
            py="xs"
            style={{ backgroundColor: themeColors.tableHeader ?? themeColors.background }}
          >
            {pill('new', preview.summary.new)}
            {pill('updated', preview.summary.updated)}
            {pill('unchanged', preview.summary.unchanged)}
            {pill('rejected', preview.summary.rejected)}
            <Text size="xs" ml="auto" style={{ color: themeColors.textSecondary }}>
              {preview.fileName} · {t('consigneeImport.rowsSeen').replace('{n}', preview.totalRows.toLocaleString())}
            </Text>
          </Group>

          {/* In the wizard the step body already scrolls, so the table grows to
              its page; in the Master modal it keeps an inner scroll at ~60vh. */}
          <ScrollArea.Autosize
            type="auto"
            scrollbarSize={8}
            mah={mode === 'wizard' ? undefined : '60vh'}
            style={{ overflowX: 'auto' }}
          >
            <Table
              horizontalSpacing="sm"
              verticalSpacing={6}
              styles={{
                th: {
                  color: themeColors.textSecondary,
                  fontSize: 11.5,
                  letterSpacing: '0.05em',
                  textTransform: 'uppercase',
                  whiteSpace: 'nowrap',
                },
                td: { whiteSpace: 'nowrap', borderTop: `1px solid ${themeColors.border}` },
              }}
            >
              <Table.Thead>
                <Table.Tr>
                  <Table.Th style={{ width: 164, minWidth: 164 }} />
                  <Table.Th style={{ minWidth: 220 }}>{t('consignee.colNama')}</Table.Th>
                  <Table.Th>{t('consignee.colNpwp')}</Table.Th>
                  <Table.Th>{t('consignee.colAlamat')}</Table.Th>
                  <Table.Th>{t('consignee.colNib')}</Table.Th>
                  <Table.Th>{t('consignee.colJenisApi')}</Table.Th>
                  <Table.Th>{t('consignee.colStatus')}</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {pagedRows.map((row) => (
                  <Table.Tr key={row.rowNumber}>
                    <Table.Td
                      style={{ boxShadow: `inset 3px 0 0 ${actionColor[row.action]}`, width: 164, minWidth: 164 }}
                    >
                      <Group gap={6} wrap="nowrap" style={{ whiteSpace: 'nowrap' }}>
                        {pill(row.action)}
                        <Text size="xs" style={{ color: themeColors.textSecondary }}>
                          {t('consigneeImport.rowLabel').replace('{n}', String(row.rowNumber))}
                        </Text>
                      </Group>
                    </Table.Td>
                    <Table.Td>
                      {renderChanged(row, 'nama', row.nama)}
                      {row.warnings && row.warnings.length > 0 && (
                        <Text size="xs" style={{ color: themeColors.textSecondary }}>
                          {row.warnings.join(' · ')}
                        </Text>
                      )}
                    </Table.Td>
                    <Table.Td>
                      <span
                        style={
                          row.action === 'rejected' && !row.npwp
                            ? { ...monoStyle, color: themeColors.error }
                            : monoStyle
                        }
                      >
                        {row.npwp || '-'}
                      </span>
                    </Table.Td>
                    <Table.Td style={{ minWidth: 240, maxWidth: 320, whiteSpace: 'normal' }}>
                      {renderChanged(row, 'alamat', row.alamat)}
                    </Table.Td>
                    <Table.Td>
                      <span style={monoStyle}>{renderChanged(row, 'nib', row.nib)}</span>
                    </Table.Td>
                    <Table.Td>{renderChanged(row, 'kodeJenisApi', row.kodeJenisApi)}</Table.Td>
                    <Table.Td>
                      {row.action === 'rejected' && row.reason ? (
                        <Text
                          size="xs"
                          style={{ color: themeColors.error, whiteSpace: 'normal', minWidth: 200, maxWidth: 280 }}
                        >
                          {row.reason}
                        </Text>
                      ) : (
                        renderChanged(row, 'kodeStatus', row.kodeStatus)
                      )}
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </ScrollArea.Autosize>

          {totalPages > 1 && (
            <Group justify="center" py="xs" style={{ borderTop: `1px solid ${themeColors.border}` }}>
              <Pagination total={totalPages} value={page} onChange={setPage} size="sm" />
            </Group>
          )}
        </div>
      )}

      {preview && (
        <Stack
          gap={4}
          style={
            mode === 'wizard'
              ? {
                  // Inside the wizard the step body scrolls and the preview can
                  // be tall; the action row sticks to the bottom of that scroll
                  // area as a bar so the import button is visible before the
                  // user scrolls. The 14px bleed matches the wizard card's padding.
                  position: 'sticky',
                  bottom: 0,
                  zIndex: 1,
                  margin: '0 -14px -14px',
                  padding: '10px 14px 14px',
                  backgroundColor: themeColors.card,
                  borderTop: `1px solid ${themeColors.border}`,
                  borderRadius: '0 0 10px 10px',
                }
              : undefined
          }
        >
          <Text size="xs" style={{ color: themeColors.textSecondary }}>
            {t('consigneeImport.neverDeletes')}
          </Text>
          <Group justify="space-between" wrap="wrap">
            <Button
              variant="outline"
              onClick={reset}
              disabled={applyMutation.isPending}
              styles={{ root: { borderColor: themeColors.border, color: themeColors.text } }}
            >
              {t('consigneeImport.chooseOther')}
            </Button>
            <Button onClick={handleApply} loading={applyMutation.isPending} disabled={importable === 0}>
              {t('consigneeImport.importN').replace('{n}', importable.toLocaleString())}
            </Button>
          </Group>
        </Stack>
      )}
    </Stack>
  );
}

/** Keeps the page and the wizard on one vocabulary for field names. */
export const importFieldLabelKey = fieldLabelKey;
